import type { AppRole, CourseRow } from '@/types'

/**
 * The single frontend source of truth for "may this role do X".
 * Route guards and templates both ask this module, so adding a role or a tenant
 * dimension later is one file to change.
 *
 * None of this is a security boundary. The API enforces authorization. These
 * checks only decide what to render, so the UI does not offer actions the
 * server will refuse.
 */

export type Capability =
  | 'course.create'
  | 'course.manageAny'
  | 'course.manageOwn'
  | 'course.publish'
  | 'course.delete'
  | 'journey.manage'
  | 'user.manage'
  | 'user.invite'
  | 'assignment.manage'
  | 'report.view'
  | 'report.viewAll'
  | 'settings.manage'
  | 'certificate.revoke'
  | 'learning.participate'

const CAPABILITIES: Record<AppRole, Capability[]> = {
  admin: [
    'course.create',
    'course.manageAny',
    'course.manageOwn',
    'course.publish',
    'course.delete',
    'journey.manage',
    'user.manage',
    'user.invite',
    'assignment.manage',
    'report.view',
    'report.viewAll',
    'settings.manage',
    'certificate.revoke',
    'learning.participate',
  ],
  instructor: [
    'course.create',
    'course.manageOwn',
    'course.publish',
    'report.view',
    'learning.participate',
  ],
  learner: ['learning.participate'],
}

export function can(role: AppRole | null, capability: Capability): boolean {
  if (!role) return false
  return CAPABILITIES[role].includes(capability)
}

export function isStaff(role: AppRole | null): boolean {
  return role === 'admin' || role === 'instructor'
}

/** Mirrors app.can_manage_course in the database. */
export function canManageCourse(
  role: AppRole | null,
  userId: string | null,
  course: Pick<CourseRow, 'instructor_id' | 'created_by'> | null,
): boolean {
  if (role === 'admin') return true
  if (role !== 'instructor' || !userId || !course) return false
  return course.instructor_id === userId || course.created_by === userId
}

/** Where a role lands after signing in. */
export function landingRoute(role: AppRole | null): string {
  return isStaff(role) ? '/admin/courses' : '/dashboard'
}
